/**
 * Why a symbol scores what it scores, in a sentence, from the numbers only.
 *
 * Assembled from thresholds applied to values the API sent and nothing else.
 * No model, no language service, and no claim that could not be checked against
 * the row it describes: every clause below is switched on by one comparison, so
 * a reader who doubts a sentence can look at the figure beside it.
 *
 * It explains suitability for a Base v1 cycle — pulls back, gets bought, comes
 * back — and never says a coin will rise. The score is not a forecast and the
 * words must not turn it into one.
 */
import { atLeast, atMost } from "@/shared/money/money";
import type { RankingCandidateResponse } from "@/shared/api";

/**
 * The thresholds, as exact decimals. Strings rather than numbers because the
 * figures they are compared against are exact decimals too, and turning either
 * side into a float would lose precision silently — which is why this codebase
 * forbids it outright.
 */
/** Above this a symbol pulls back often enough to keep a slot working. */
const FREQUENT_PULLBACKS = "0.04";
/** Below this it barely moves, which starves the strategy of round trips. */
const RARE_PULLBACKS = "0.015";
/** Above this most pullbacks are bought back, which is the whole thesis. */
const RELIABLE_RECOVERY = "0.7";
/** Below this a fall is as likely to stay a fall. */
const WEAK_RECOVERY = "0.55";
/** Bars. Below this the round trip is quick enough to free the capital soon. */
const QUICK_RECOVERY = 6;
/** Above this the symbol spends most of its time falling. */
const ONE_SIDED = "0.5";
/** Quote volume a day. Below it, size starts moving the price. */
const AMPLE_LIQUIDITY = "40000000";
/** Fraction. Above it, the round trip pays the spread more than once over. */
const WIDE_SPREAD = "0.001";

/** What the market structure says. Empty when nothing stands out either way. */
function marketReasons(candidate: RankingCandidateResponse): string[] {
  const reasons: string[] = [];

  if (
    candidate.pullback_rate != null &&
    atLeast(candidate.pullback_rate, FREQUENT_PULLBACKS)
  ) {
    reasons.push("частые откаты");
  } else if (
    candidate.pullback_rate != null &&
    atMost(candidate.pullback_rate, RARE_PULLBACKS)
  ) {
    reasons.push("редкие откаты");
  }

  if (
    candidate.recovery_rate != null &&
    atLeast(candidate.recovery_rate, RELIABLE_RECOVERY)
  ) {
    reasons.push("высокая доля восстановлений");
  } else if (
    candidate.recovery_rate != null &&
    atMost(candidate.recovery_rate, WEAK_RECOVERY)
  ) {
    reasons.push("низкая доля восстановлений");
  }

  if (
    candidate.median_recovery_bars !== null &&
    candidate.median_recovery_bars !== undefined &&
    candidate.median_recovery_bars <= QUICK_RECOVERY
  ) {
    reasons.push("короткое восстановление");
  }

  if (
    candidate.decline_persistence != null &&
    atLeast(candidate.decline_persistence, ONE_SIDED)
  ) {
    reasons.push("длительные односторонние падения");
  }

  reasons.push(
    candidate.turnover != null && atLeast(candidate.turnover, AMPLE_LIQUIDITY)
      ? "достаточная ликвидность"
      : "умеренная ликвидность",
  );

  if (candidate.spread != null && atLeast(candidate.spread, WIDE_SPREAD)) {
    reasons.push("широкий спред");
  }

  return reasons;
}

function sentence(parts: string[]): string {
  const [last, ...rest] = [...parts].reverse();
  if (last === undefined) return "";
  if (rest.length === 0) return `${last}.`;
  return `${rest.reverse().join(", ")} и ${last}.`;
}

/**
 * One sentence about this row, or an empty string when there is nothing to say
 * that the row does not already say itself.
 */
export function explain(candidate: RankingCandidateResponse): string {
  const basis =
    candidate.ranking_mode === "TURNOVER_HISTORY"
      ? `Оценка по собственной истории: ${String(candidate.completed_cycle_count)} завершённых циклов.`
      : `Оценка по структуре рынка: ${sentence(marketReasons(candidate))}`;

  switch (candidate.status) {
    case "ACTIVE":
      return `${basis} Сейчас торгуется${
        candidate.active_slot_id === null ? "" : ` в слоте ${candidate.active_slot_id}`
      } и не может занять ещё один слот.`;
    case "COOLDOWN":
      return `${basis} Монета отдыхает после закрытия цикла и пока не может получить слот.`;
    case "QUARANTINED":
      return `${basis} Монета в карантине после аварийного закрытия${
        candidate.quarantine_reason === null
          ? ""
          : `: ${candidate.quarantine_reason}`
      }.`;
    case "QUARANTINED_REVIEW_REQUIRED":
      return `${basis} Монета в карантине, причина не была записана — автоматически он не снимется, нужна проверка.`;
    case "BLOCKED":
      return `${basis} Монета отклонена фильтрами${
        candidate.reason_code === null ? "" : `: ${candidate.reason_code}`
      }.`;
    default:
      return basis;
  }
}
